/**
 * हिन्दी — the SHELL, in the words a shopkeeper actually reads.
 *
 * Scope on purpose: the sidebar (every label and group title), the mobile
 * bottom nav, the top bar, the command palette's group names, the Today page,
 * the Store Settings header + section names, and the common actions. Pages
 * that are not listed here fall back to English through `t()`.
 *
 * House style:
 *   • Devanagari, never transliterated English in Latin script.
 *   • Terms merchants SAY in English stay in Latin script inside the Hindi
 *     sentence — Order, Invoice, GST, UPI, Khata, WhatsApp, SEO, P&L.
 *   • Respectful imperative (करें, देखें), the way a helpful assistant speaks.
 *   • ~150 correct strings beat 1,500 machine ones. Add a string when a
 *     surface is actually wired to `t()`, not before.
 */
const hi = {
  // ── Common actions ────────────────────────────────────────────────────────
  'action.save': 'सेव करें',
  'action.saving': 'सेव हो रहा है…',
  'action.cancel': 'रद्द करें',
  'action.delete': 'हटाएँ',
  'action.remove': 'निकालें',
  'action.add': 'जोड़ें',
  'action.search': 'खोजें',
  'action.filters': 'फ़िल्टर',
  'action.done': 'हो गया',
  'action.continue': 'आगे बढ़ें',
  'action.next': 'अगला',
  'action.back': 'पीछे',
  'action.close': 'बंद करें',
  'action.edit': 'बदलें',
  'action.print': 'प्रिंट करें',
  'action.share': 'शेयर करें',
  'action.export': 'एक्सपोर्ट करें',
  'action.download': 'डाउनलोड करें',
  'action.upload': 'अपलोड करें',
  'action.copy': 'कॉपी करें',
  'action.send': 'भेजें',
  'action.view': 'देखें',
  'action.open': 'खोलें',
  'action.apply': 'लागू करें',
  'action.clear': 'साफ़ करें',
  'action.confirm': 'पक्का करें',
  'action.discard': 'बदलाव हटाएँ',
  'action.refresh': 'रीफ़्रेश करें',
  'action.try_again': 'फिर कोशिश करें',
  'action.loading': 'लोड हो रहा है…',
  'action.no_results': 'कुछ नहीं मिला',
  'action.unsaved': 'आपके बदलाव अभी सेव नहीं हुए',
  'action.yes': 'हाँ',
  'action.no': 'नहीं',
  'action.later': 'बाद में',

  // ── Language ──────────────────────────────────────────────────────────────
  'lang.label': 'भाषा',
  'lang.english': 'English',
  'lang.hindi': 'हिन्दी',
  'lang.switch_aria': 'पोर्टल की भाषा बदलें',

  // ── Sidebar: groups ───────────────────────────────────────────────────────
  'nav.group.sell': 'बिक्री',
  'nav.group.catalog': 'सामान और स्टॉक',
  'nav.group.customers': 'ग्राहक',
  'nav.group.money': 'पैसा',
  'nav.group.store': 'दुकान और सेटअप',
  'nav.group.site': 'मेरी वेबसाइट',
  'nav.group.growth': 'ग्रोथ',
  'nav.group.account': 'अकाउंट',
  // Mobile "More" sheet groups.
  'nav.group.products': 'सामान',
  'nav.group.orders': 'Orders',
  'nav.group.business': 'बिज़नेस',
  'nav.group.store_short': 'दुकान',

  // ── Sidebar / bottom nav: items ───────────────────────────────────────────
  'nav.today': 'आज',
  'nav.day_summary': 'दिन का सारांश',
  'nav.products': 'सामान',
  'nav.add_product': 'नया सामान जोड़ें',
  'nav.add': 'जोड़ें',
  'nav.all_products': 'सारा सामान',
  'nav.catalog': 'कैटलॉग',
  'nav.catalog_settings': 'कैटलॉग सेटिंग',
  'nav.inventory': 'स्टॉक',
  'nav.recipes': 'रेसिपी और यील्ड',
  'nav.item_recipes': 'आइटम रेसिपी',
  'nav.merchandising': 'कलेक्शन और डिस्प्ले',
  'nav.collections': 'कलेक्शन',
  'nav.categories': 'कैटेगरी',
  'nav.discounts': 'छूट',
  'nav.coupons': 'कूपन',
  'nav.counter': 'काउंटर बिलिंग',
  'nav.order_board': 'Order बोर्ड',
  'nav.appointments': 'अपॉइंटमेंट',
  'nav.orders': 'Orders',
  'nav.website_orders': 'वेबसाइट Orders',
  'nav.all_orders': 'सभी Orders',
  'nav.unified_inbox': 'सभी Orders',
  'nav.order_book': 'Order बुक',
  'nav.whatsapp': 'WhatsApp',
  'nav.custom_requests': 'कस्टम रिक्वेस्ट',
  'nav.enquiries': 'पूछताछ',
  'nav.customers': 'ग्राहक',
  'nav.reviews': 'रिव्यू',
  'nav.loyalty': 'लॉयल्टी और इनाम',
  'nav.campaigns': 'कैंपेन',
  'nav.email_marketing': 'ईमेल मार्केटिंग',
  'nav.automations': 'ऑटोमेशन',
  'nav.subscriptions': 'सब्सक्रिप्शन',
  'nav.returns': 'रिटर्न',
  'nav.leads': 'लीड्स',
  'nav.quotations': 'कोटेशन',
  'nav.payments_dues': 'पेमेंट और बकाया',
  'nav.payments': 'पेमेंट',
  'nav.payment_setup': 'पेमेंट सेटअप',
  'nav.connect_payments': 'पेमेंट जोड़ें',
  'nav.earnings': 'कमाई',
  'nav.books': 'हिसाब-किताब',
  'nav.billing_documents': 'बिलिंग दस्तावेज़',
  'nav.expenses': 'खर्च',
  'nav.suppliers': 'सप्लायर',
  'nav.purchases': 'खरीद',
  'nav.reports': 'रिपोर्ट',
  'nav.pnl_report': 'P&L रिपोर्ट',
  'nav.gstr1': 'GSTR-1',
  'nav.goals': 'लक्ष्य',
  'nav.website': 'वेबसाइट',
  'nav.edit_site': 'अपनी साइट बदलें',
  'nav.my_work': 'मेरा काम',
  'nav.themes': 'थीम',
  'nav.pages_content': 'पेज और कंटेंट',
  'nav.seo': 'SEO',
  'nav.analytics': 'एनालिटिक्स',
  'nav.production': 'प्रोडक्शन',
  'nav.artisans': 'कारीगर',
  'nav.team': 'टीम',
  'nav.brand_profile': 'ब्रांड प्रोफ़ाइल',
  'nav.profile': 'प्रोफ़ाइल',
  'nav.link_in_bio': 'Link in Bio',
  'nav.store_settings': 'दुकान की सेटिंग',
  'nav.plan_billing': 'प्लान और बिलिंग',
  'nav.plan': 'प्लान',
  'nav.features': 'फ़ीचर',
  'nav.personalize': 'पर्सनलाइज़',
  'nav.team_roles': 'टीम और रोल',
  'nav.licences_files': 'लाइसेंस और फ़ाइलें',
  'nav.trash': 'ट्रैश',
  'nav.support': 'सहायता',
  'nav.logout': 'लॉग आउट',
  'nav.more': 'और',
  'nav.filter_menu': 'मेन्यू में खोजें…',
  'nav.clear_filter': 'खोज साफ़ करें',
  'nav.no_match': '“{q}” से कुछ नहीं मिला।',
  'nav.open_menu': 'मेन्यू खोलें',
  'nav.close_menu': 'मेन्यू बंद करें',
  'nav.home_aria': 'Inikru होम',
  'nav.pro': 'Pro',

  // ── Subscription prompt (the locked Upload item) ──────────────────────────
  'sub.trial_ended_title': 'आपका फ़्री ट्रायल खत्म हो गया',
  'sub.trial_ended_text': 'सामान अपलोड करते रहने और दुकान चलाते रहने के लिए प्लान रिन्यू करें।',
  'sub.start_trial_title': 'अपना फ़्री ट्रायल शुरू करें',
  'sub.start_trial_text': 'सामान अपलोड करने और दुकान बनाने के लिए 15 दिन का फ़्री ट्रायल शुरू करें।',
  'sub.renew_now': 'अभी रिन्यू करें',
  'sub.start_trial': 'फ़्री ट्रायल शुरू करें',
  'sub.view_plans': 'प्लान देखें',
  'sub.session_expired': 'सेशन खत्म हो गया',
  'sub.login_again': 'कृपया दोबारा लॉगिन करें।',

  // ── Top bar ───────────────────────────────────────────────────────────────
  'topbar.search_placeholder': 'खोजें या किसी स्क्रीन पर जाएँ…',
  'topbar.search_aria': 'खोजें और किसी भी स्क्रीन पर जाएँ',

  // ── Approvals bell ────────────────────────────────────────────────────────
  'approvals.aria': 'Approvals',
  'approvals.aria_count': '{n} approvals आपके इंतज़ार में',
  'approvals.waiting': 'आपके फ़ैसले का इंतज़ार',
  'approvals.none': 'कोई फ़ैसला बाकी नहीं।',
  'approvals.open': 'Approvals खोलें',
  'approvals.see_all': 'सभी {n} देखें',
  'approvals.kind.purchase': 'खरीद',
  'approvals.kind.discount': 'छूट',
  'approvals.kind.refund': 'रिफंड',
  'approvals.kind.other': 'रिक्वेस्ट',
  'approvals.raised_by': '{name} ने भेजा',
  'approvals.team_member': 'टीम के सदस्य',

  // ── Command palette ───────────────────────────────────────────────────────
  'palette.aria': 'कमांड पैलेट',
  'palette.placeholder': 'किसी स्क्रीन पर जाएँ, या अपना सामान, ग्राहक और orders खोजें…',
  'palette.no_matches': 'कुछ नहीं मिला।',
  'palette.navigate': 'ऊपर-नीचे',
  'palette.open': 'खोलें',
  'palette.close': 'बंद',
  'palette.close_aria': 'कमांड पैलेट बंद करें',
  'palette.group.today': 'आज',
  'palette.group.sell': 'बिक्री',
  'palette.group.catalog': 'सामान',
  'palette.group.customers': 'ग्राहक',
  'palette.group.money': 'पैसा',
  'palette.group.store': 'दुकान और सेटअप',
  'palette.pill.product': 'सामान',
  'palette.pill.customer': 'ग्राहक',
  'palette.pill.order': 'Order',

  // ── Today ─────────────────────────────────────────────────────────────────
  'today.title': 'आज',
  'today.subtitle': 'अभी आपको किस पर ध्यान देना है, और दिन कैसा चल रहा है।',
  'today.refresh': 'रीफ़्रेश करें',
  'today.band.needs_you': 'अभी ज़रूरी',
  'today.band.needs_you_hint': 'हर नंबर असली है, और हर एक पर आप कुछ कर सकते हैं।',
  'today.band.how_going': 'आज का दिन कैसा जा रहा है',
  'today.band.how_going_hint': 'सिर्फ़ देखने के लिए — आज का कारोबार एक नज़र में।',
  'today.band.your_store': 'आपकी दुकान',
  'today.band.your_store_hint': 'दुकान की बाकी हर चीज़ दुकान की सेटिंग में है।',
  'today.open': 'खोलें',
  'today.failed_title': 'आज का डेटा लोड नहीं हुआ',
  'today.failed_body': 'बाकी सब चल रहा है — थोड़ी देर में फिर कोशिश करें।',
  'today.empty_title': 'अभी कुछ भी ज़रूरी नहीं',
  'today.empty_body': 'कोई order बाकी नहीं, कोई बकाया नहीं, कुछ खत्म नहीं हो रहा। जाइए, बिक्री कीजिए।',
  'today.empty_body_site': 'आपकी साइट चल रही है और कुछ ठीक करने को नहीं। जाइए, कुछ ऐसा बनाइए जो इसमें जोड़ने लायक हो।',
  'today.add_product': 'सामान जोड़ें',
  'today.view_store': 'मेरी दुकान देखें',
  'today.store_settings': 'दुकान की सेटिंग',

  // ── Store settings (header + section nav + language row) ──────────────────
  'settings.eyebrow': 'आपकी दुकान',
  'settings.title': 'दुकान की सेटिंग',
  'settings.subtitle': 'यहाँ की हर चीज़ आपके ग्राहकों को दिखती है — डिलीवरी चार्ज, रिटर्न लेते हैं या नहीं, वे कैसे पेमेंट करें। एक हिस्सा चुनें, बदलें, सेव करें।',
  'settings.sections_aria': 'सेटिंग के हिस्से',
  'settings.section.general': 'सामान्य',
  'settings.section.billing': 'प्लान और बिलिंग',
  'settings.section.money': 'पैसा',
  'settings.section.delivery': 'डिलीवरी',
  'settings.section.courier': 'कूरियर',
  'settings.section.returns': 'रिटर्न',
  'settings.section.tracking': 'Ad ट्रैकिंग',
  'settings.section.widgets': 'चैट और टूल्स',
  'settings.section.offers': 'ऑफ़र',
  'settings.section.advanced': 'एडवांस्ड',
  'settings.unsaved_in': '{sections} में बदलाव सेव नहीं हुए',
  'settings.needs_fixing': 'एक जगह ठीक करनी है — सेव दबाएँ, हम वहाँ ले चलेंगे',
  'settings.saved_toast': 'सेव हो गया — आपकी दुकान अपडेट है',
  'settings.language.title': 'भाषा',
  'settings.language.desc': 'यह पोर्टल किस भाषा में दिखे। आपकी दुकान की वेबसाइट पर इसका कोई असर नहीं पड़ता।',
  'settings.language.note': 'तुरंत लागू होता है — सेव करने की ज़रूरत नहीं।',
  'settings.general.title': 'सामान्य',
  'settings.general.desc': 'भाषा, और आपकी दुकान का QR।',
};

export default hi;
